import React from "react";
import * as UI from "./ui.jsx";
import { useInspectionData } from "./inspection_display.js";
// Stable — draft grid/table with box snapshots, box-map assignment, horse profile

const { useState: useStateS, useMemo: useMemoS } = React;

function StableView({ go, initialMode = "draft" }) {
  const { Icon, Tag, Card, BoxSnap, useBoxAssign, ModuleGate, MockNote } = UI;
  const { HORSES } = window.DATA;
  const [mode, setMode] = useStateS(initialMode); // draft | boxes
  const [query, setQuery] = useStateS("");
  const [view, setView] = useStateS("grid");
  const [sort, setSort] = useStateS("lot");
  // Vendor-wide display setting. "Most inspected" cannot survive it: leaving a
  // sort in place that orders by a figure the page no longer shows would order
  // the draft by something invisible.
  const showInspections = useInspectionData();
  const [assign, setAssign] = useBoxAssign();
  const hasCameras = window.VD.source("stable.camera_media") !== "unavailable";

  const filtered = HORSES.filter(h =>
    !query || (h.name + " " + h.lot + " " + h.sire + " " + h.dam).toLowerCase().includes(query.toLowerCase())
  ).sort((a, b) => {
    if (!!a.withdrawn !== !!b.withdrawn) return a.withdrawn ? 1 : -1;
    if (sort === "lot") return (+a.lot) - (+b.lot);
    if (sort === "views") return b.views - a.views;
    if (sort === "watch") return b.watchlist - a.watchlist;
    if (sort === "insp") return showInspections ? b.inspections - a.inspections : (+a.lot) - (+b.lot);
    return 0;
  });

  return (
    <ModuleGate modules="foundation.sale_lot_mirror">
      <div className="fade-in">
        <div className="toolbar" style={{ marginBottom: 20 }}>
          <div className="seg">
            <button className={mode === "draft" ? "on" : ""} onClick={() => setMode("draft")}>Draft</button>
            {hasCameras && <button className={mode === "boxes" ? "on" : ""} onClick={() => setMode("boxes")}>Box map</button>}
          </div>
          {mode === "draft" && (
            <>
              <div className="searchbox">
                <Icon.search style={{ width: 16, height: 16, color: "var(--fg-muted)" }}/>
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search lot, sire, dam…"
                       style={{ flex: 1, background: "transparent", border: 0, outline: 0, fontSize: 14 }}/>
              </div>
              <div className="seg hide-mobile">
                <button className={sort === "lot" ? "on" : ""} onClick={() => setSort("lot")}>Lot order</button>
                <button className={sort === "views" ? "on" : ""} onClick={() => setSort("views")}>Most viewed</button>
                <button className={sort === "watch" ? "on" : ""} onClick={() => setSort("watch")}>Most watched</button>
                {showInspections && <button className={sort === "insp" ? "on" : ""} onClick={() => setSort("insp")}>Most inspected</button>}
              </div>
              <div style={{ flex: 1 }}/>
              <MockNote module="foundation.sale_lot_mirror"/>
              <div className="seg hide-mobile">
                <button className={view === "grid" ? "on" : ""} onClick={() => setView("grid")}>Grid</button>
                <button className={view === "table" ? "on" : ""} onClick={() => setView("table")}>Table</button>
              </div>
            </>
          )}
        </div>

        {mode === "boxes" && <BoxMap assign={assign} setAssign={setAssign} go={go}/>}

        {mode === "draft" && view === "grid" && (
          <div className="grid cols-3">
            {filtered.map(h => (
              <div key={h.id} className="horse-card" onClick={() => go("horse:" + h.id)} style={h.withdrawn ? { opacity: 0.62 } : undefined}>
                <div className="photo" style={{ height: 150 }}>
                  <BoxSnap box={assign[h.lot]} style={{ position: "absolute", inset: 0 }}/>
                  <span className="lot">LOT {h.lot}</span>
                  {h.withdrawn && <span style={{ position: "absolute", top: 12, right: 12 }}><Tag variant="ribbon">Withdrawn</Tag></span>}
                  {!h.withdrawn && h.hot && <span style={{ position: "absolute", top: 12, right: 12 }}><Tag variant="gold">★ Hot</Tag></span>}
                </div>
                <div className="body">
                  <div className="name" style={{ fontSize: 19 }}>{h.name}</div>
                  <div className="ped">{h.colour} {h.sex} · foaled {h.dob}</div>
                  {h.withdrawn ? (
                    <div className="muted" style={{ marginTop: 12, fontSize: 13 }}>Withdrawn from sale</div>
                  ) : (
                    <>
                      <div className="row" style={{ marginTop: 10, gap: 6 }}>
                        <Tag>{assign[h.lot] ? `Box K${assign[h.lot]}` : "No box"}</Tag>
                        <Tag>{h.sex}</Tag>
                      </div>
                      {h.statsAvailable && (
                        <div className="mini">
                          <div><div className="v">{h.views.toLocaleString()}</div><div className="l">Views</div></div>
                          <div><div className="v">{h.plays}</div><div className="l">Breeze</div></div>
                          <div><div className="v">{h.watchlist}</div><div className="l">Watch</div></div>
                          {showInspections && <div><div className="v">{h.inspections}</div><div className="l">Insp.</div></div>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {mode === "draft" && view === "table" && (
          <Card className="flush">
            <table className="table">
              <thead><tr>
                <th>Lot</th><th>Pedigree</th><th>Sex</th><th>Box</th>
                <th style={{ textAlign: "right" }}>Views</th><th style={{ textAlign: "right" }}>Breeze</th>
                <th style={{ textAlign: "right" }}>Watch</th>{showInspections && <th style={{ textAlign: "right" }}>Insp.</th>}<th>Trend</th>
              </tr></thead>
              <tbody>
                {filtered.map(h => (
                  <tr key={h.id} className="click" onClick={() => go("horse:" + h.id)} style={h.withdrawn ? { opacity: 0.55 } : undefined}>
                    <td style={{ fontFamily: "var(--font-display)", color: "var(--inglis-blue)", fontSize: 16 }}>{h.lot}</td>
                    <td style={{ color: "var(--fg-1)", fontSize: 15 }}>{h.name}{!h.withdrawn && h.hot && <span style={{ marginLeft: 8 }}><Tag variant="gold">★</Tag></span>}</td>
                    <td>{h.sex}</td>
                    <td>{assign[h.lot] ? `K${assign[h.lot]}` : "—"}</td>
                    <td style={{ textAlign: "right", color: "var(--fg-1)" }}>{h.views.toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>{h.plays}</td>
                    <td style={{ textAlign: "right" }}>{h.watchlist}</td>
                    {showInspections && <td style={{ textAlign: "right" }}>{h.inspections}</td>}
                    <td style={{ width: 90 }}><UI.Sparkline data={h.trend} height={28}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </ModuleGate>
  );
}

// =================== Box map — assign horses to boxes ===================
function BoxMap({ assign, setAssign, go }) {
  const { Card, Tag, Icon } = UI;
  const { HORSES, BOX_IDS, BOX_BLOCKS } = window.DATA;
  const [picked, setPicked] = useStateS(null); // lot string picked for placement

  const lotInBox = useMemoS(() => {
    const m = {};
    for (const [lot, box] of Object.entries(assign)) m[box] = lot;
    return m;
  }, [assign]);
  const unassigned = HORSES.filter(h => !h.withdrawn && !assign[h.lot]);

  const place = (box) => {
    const occupant = lotInBox[box];
    if (picked) {
      const next = { ...assign };
      if (occupant && occupant !== picked) return; // occupied — pick the occupant instead
      next[picked] = box;
      setAssign(next); setPicked(null);
    } else if (occupant) {
      setPicked(occupant);
    }
  };
  const clearBox = (box) => {
    const occupant = lotInBox[box];
    if (!occupant) return;
    const next = { ...assign }; delete next[occupant];
    setAssign(next); setPicked(null);
  };

  const blocks = BOX_BLOCKS.length ? BOX_BLOCKS : [{ label: "Your allocation", boxes: BOX_IDS }];

  return (
    <div className="grid boxmap-grid" style={{ gridTemplateColumns: "1fr 320px", gap: 22, alignItems: "start" }}>
      <Card title={`${window.DATA.VENDOR.barn} · your allocation`} subtitle="Tap a horse, then an empty box to place it. Tap an occupied box to move or clear.">
        {blocks.map(bl => (
          <div key={bl.label} style={{ marginBottom: 18 }}>
            <div className="eyebrow" style={{ fontSize: 10, marginBottom: 8 }}>{bl.label}</div>
            <div className="boxrow">
              {bl.boxes.map(b => {
                const lot = lotInBox[b];
                const isPicked = picked && lot === picked;
                return (
                  <button key={b} className={`box ${lot ? "occ" : ""} ${isPicked ? "picked" : ""} ${picked && !lot ? "target" : ""}`}
                          onClick={() => place(b)}>
                    <span className="bid">K{b}</span>
                    {lot ? <span className="blot">Lot {lot}</span> : <span className="bfree">{picked ? "Place here" : "Empty"}</span>}
                    {lot && isPicked && (
                      <span className="bactions">
                        <span onClick={(e) => { e.stopPropagation(); clearBox(b); }}>Clear</span>
                        <span onClick={(e) => { e.stopPropagation(); go("horse:L" + lot); }}>Open</span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </Card>

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <Card title={`To place · ${unassigned.length}`} subtitle={unassigned.length ? "Tap a horse, then its box." : "Every lot has a box."}>
          {unassigned.length === 0 && <div className="row" style={{ gap: 8 }}><Icon.check style={{ width: 18, height: 18, color: "var(--positive)" }}/><span style={{ fontSize: 14 }}>Draft fully boxed.</span></div>}
          {unassigned.map(h => (
            <button key={h.id} className="placerow" data-picked={picked === h.lot ? "1" : undefined} onClick={() => setPicked(picked === h.lot ? null : h.lot)}>
              <span className="display" style={{ fontSize: 19, color: "var(--inglis-blue)", width: 52 }}>Lot {h.lot}</span>
              <span style={{ flex: 1, textAlign: "left" }}>
                <span style={{ display: "block", fontSize: 13.5 }}>{h.name}</span>
                <span className="muted" style={{ fontSize: 11 }}>{h.sex}</span>
              </span>
              <Tag variant={picked === h.lot ? "ribbon" : ""}>{picked === h.lot ? "Placing…" : "Place"}</Tag>
            </button>
          ))}
        </Card>
        <Card title="How cameras map" subtitle="Ten “Box K” cameras cover the barn — each frames a block of boxes, two rows of four. Assigning a horse to a box is what labels its crop.">
          <div className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
            Tapping a box opens its live cropped stream on demand; thumbnails refresh about every {window.DATA.SNAPSHOT_INTERVAL} minutes and everything is archived. Assignments can be changed at any time.
          </div>
          <div className="divider"/>
          <div className="caption">
            Box placement is held in this browser only — there is no assignment provider yet.
          </div>
        </Card>
      </div>
    </div>
  );
}

export { StableView };
